import { useEffect, useMemo, useRef, useState } from "react";

import { history } from "@state";

type ScrollContainer = HTMLElement | null | Window;

function getScrollTop(container: ScrollContainer): number {
	if (container instanceof Window) {
		return container.scrollY;
	}

	return container?.scrollTop || 0;
}

function getClientHeight(container: ScrollContainer): number {
	if (container instanceof Window) {
		return container.document.documentElement.clientHeight;
	}

	return container?.clientHeight || 0;
}

export function useReturnToScroll(
	isLoading: boolean,
	containerRef: ScrollContainer
): boolean {
	const timerId = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	);
	const counter = useRef<number>(0);
	const isWorkingRef = useRef<boolean>(true);
	const [isWorking, setIsWorking] = useState<boolean>(true);
	const lastContainerHeight = useRef<number>(getClientHeight(containerRef));
	isWorkingRef.current = isWorking;
	const debounceTimeout = useRef<number>(0);

	const scrollAction = useMemo(
		() => () => {
			if (isWorkingRef.current) {
				return;
			}
			clearTimeout(debounceTimeout.current);
			// @ts-expect-error
			debounceTimeout.current = setTimeout(() => {
				history.replace(
					history.location.pathname + history.location.search,
					Object.assign(history.location.state || {}, {
						scrollPositionApp: getScrollTop(containerRef),
					})
				);
			}, 50);
		},
		[]
	);

	useEffect(() => {
		containerRef?.addEventListener("scroll", scrollAction);
		return () => {
			if (timerId.current != null) {
				clearInterval(timerId.current);
			}
			containerRef?.removeEventListener("scroll", scrollAction);
		};
	}, []);

	if (isLoading) {
		if (timerId.current != null) {
			clearInterval(timerId.current);
			timerId.current = undefined;
		}
		return isWorking;
	}

	const lastScroll: number = history.location.state?.scrollPositionApp || 0;
	if (timerId.current == null && isWorking) {
		timerId.current = setInterval(() => {
			containerRef?.scrollTo(0, lastScroll);
			counter.current++;

			const clientHeight = getClientHeight(containerRef);

			if (
				(lastScroll === getScrollTop(containerRef) &&
					lastContainerHeight.current) ||
				0 === clientHeight ||
				counter.current >= 20
			) {
				setIsWorking(false);
				clearInterval(timerId.current);
				timerId.current = undefined;
			}

			lastContainerHeight.current = clientHeight;
		}, 100);
	}

	return isWorking;
}
